import { Component, effect, input, isDevMode, OnInit, output, ChangeDetectionStrategy } from '@angular/core';
import { AgCellWrapperComponent, AgFloatingFilterWrapperComponent } from '@methylium/ag-grid';

import { AgGridModule } from 'ag-grid-angular';
import {
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  ColumnState,
  DomLayoutType,
  FilterChangedEvent,
  FilterModel,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from 'ag-grid-community';
import { CustomAgGridHeaderComponent } from '../custom-ag-grid-header/custom-ag-grid-header.component';
import { ListLoadingOverlayComponent } from '../list-loading-overlay/list-loading-overlay.component';
import { ListNoRowsResultComponent } from '../list-no-rows-result/list-no-rows-result.component';
import { getDutchLocaleEntries } from '../lists.localization';
import { NumericCellEditorComponent } from '../numeric-cell-editor/numeric-cell-editor.component';

@Component({
  selector: 'ml-list-simple',
  templateUrl: './list-simple.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AgGridModule],
})
export class ListSimpleComponent implements OnInit {
  readonly columnDefinitions = input.required<(ColDef | ColGroupDef)[]>();
  readonly dataRows = input.required<unknown[]>();
  readonly domLayout = input<DomLayoutType>('autoHeight');
  readonly enableSorting = input(false);
  readonly enableFilter = input(false);
  readonly initialFilterModel = input<FilterModel | undefined>(undefined);
  readonly initialSortModel = input<ColumnState[] | undefined>(undefined);
  readonly rowNodeIdProperty = input('id');
  readonly headerHeight = input<number | undefined>(undefined);
  readonly rowHeight = input<number | undefined>(undefined);

  readonly cellClicked = output<CellClickedEvent>();
  readonly gridReady = output<GridReadyEvent>();
  readonly filterChanged = output<FilterChangedEvent>();

  public gridOptions: GridOptions;
  public components: Record<string, unknown>;

  private gridApi: GridApi;

  ngOnInit() {
    this.components = {
      agFloatingFilterWrapperComponent: AgFloatingFilterWrapperComponent,
      agCellWrapperComponent: AgCellWrapperComponent,
      agNumericCellEditorComponent: NumericCellEditorComponent,
    };

    const rowNodeIdProperty = this.rowNodeIdProperty();
    this.gridOptions = {
      debug: isDevMode(),
      localeText: getDutchLocaleEntries(),
      enableBrowserTooltips: true,
      multiSortKey: 'ctrl',
      suppressRowClickSelection: false,
      rowMultiSelectWithClick: true,
      suppressHorizontalScroll: false,
      suppressAutoSize: false,
      rowSelection: 'multiple',
      rowModelType: 'clientSide',
      stopEditingWhenCellsLoseFocus: true,
      undoRedoCellEditing: true,
      columnDefs: [],
      rowData: [],
      loadingOverlayComponent: 'customLoadingOverlay',
      noRowsOverlayComponent: 'customNoRowsOverlay',
      defaultColDef: {
        resizable: true,
        filter: true,
        headerComponentParams: {
          resizable: true,
          filter: true,
          headerComponent: CustomAgGridHeaderComponent,
        },
      },
      components: {
        customLoadingOverlay: ListLoadingOverlayComponent,
        customNoRowsOverlay: ListNoRowsResultComponent,
      },
      getRowId(item) {
        return item.data[rowNodeIdProperty];
      },
    };

    this.setColumnDefinitions(this.columnDefinitions());

    this.gridOptions.rowData = this.dataRows(); // initializes row data

    const domLayout = this.domLayout();
    this.gridOptions.domLayout = domLayout === 'normal' ? domLayout : 'autoHeight';
  }

  constructor() {
    effect(() => {
      if (!this.gridOptions) {
        return;
      }
      const columnDefinitions = this.columnDefinitions();
      this.setColumnDefinitions(columnDefinitions);
    });

    effect(() => {
      if (!this.gridOptions) {
        return;
      }
      const dataRows = this.dataRows();
      if (this.gridApi) {
        this.gridApi.setGridOption('rowData', dataRows); // updates row data
      } else {
        this.gridOptions.rowData = dataRows;
      }
    });
  }

  private setColumnDefinitions(columnDefinitions: (ColDef | ColGroupDef)[]) {
    this.gridOptions.columnDefs = columnDefinitions;
    if (this.gridOptions.defaultColDef) {
      this.gridOptions.defaultColDef.sortable = this.enableSorting();
      this.gridOptions.defaultColDef.floatingFilter = this.enableFilter();
    }
  }

  onGridReady(readyParams: GridReadyEvent) {
    this.gridApi = readyParams.api;
    readyParams.api.sizeColumnsToFit();

    window.addEventListener('resize', () => {
      setTimeout(() => {
        readyParams.api.sizeColumnsToFit();
      });
    });

    this.gridApi.setFilterModel(this.initialFilterModel() ?? null);
    this.gridReady.emit(readyParams);
  }

  onFilterChanged(gridParams: FilterChangedEvent) {
    this.filterChanged.emit(gridParams);
  }

  onCellClicked(cellParams: CellClickedEvent) {
    this.cellClicked.emit(cellParams);
  }

  firstDataRendered(event: FirstDataRenderedEvent) {
    event.api.sizeColumnsToFit();
  }
}
